import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { eOrderType, Order, UboTreeNode, UboTreeNodesService, UserRepositoryService } from 'reg-hub-common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { Constants } from 'projects/reg-hub-client/src/constants';

@Component({
  selector: 'app-confirm-submit',
  templateUrl: './confirm-submit.component.html',
  styleUrls: ['./confirm-submit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ConfirmSubmitComponent implements OnInit {
  protected order?: Order;
  protected showEmailOption? = false;
  protected showReturnCheckbox? = false;
  protected showLinkToHubReportCheckboxes = false;
  protected linkToUboTrees?: UboTreeNode[];
  protected uboTreeToLinkTo?: UboTreeNode;
  protected uboTreeNodesToLinkTo?: UboTreeNode[];
  protected customMessage?: string | undefined;

  protected sendEmail = false;
  protected shouldReturnToBusinessSearchResults: boolean = false;

  protected emails: string[] = [];
  // The email the user is currently typing, not yet turned into a chip
  protected pendingEmail = new FormControl('');
  // Holds the validation state of the whole list so mat-form-field can display errors
  protected emailsControl = new FormControl<string[]>([]);
  protected separatorKeyCodes: number[] = [ENTER, COMMA, SEMICOLON, SPACE];

  // Commas, semicolons and whitespace all split one entry from the next
  private static readonly emailSeparators = /[,;\s]+/;
  private authenticatedUserEmail?: string;

  constructor(
    private userService: UserRepositoryService,
    private uboTreeService: UboTreeNodesService,
    private dialogRef: MatDialogRef<ConfirmSubmitComponent>,
    @Inject(MAT_DIALOG_DATA) data?: { order: Order, returnOption?: boolean, customMessage?: string; }) {

    this.order = data?.order ?? undefined;
    this.showReturnCheckbox = data?.returnOption ?? false;
    this.customMessage = data?.customMessage;
  }

  ngOnInit(): void {
    this.showEmailOption = this.order?.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport;

    if (this.showEmailOption) {
      this.userService.getAuthenticatedUser().subscribe(user => {
        this.authenticatedUserEmail = user.contactDetails?.email;
        this.prefillAuthenticatedUserEmail();
      });
    }

    // Clear the invalid-email error while the user is correcting their input
    this.pendingEmail.valueChanges.subscribe(() => this.clearInvalidEmailError());

    if (this.order?.orderTypeID === eOrderType.businessReport &&
      this.uboTreeService.linkToHubReportNodes.value.length > 0 &&
      !this.order?.businessReportCriteria?.uboTreeNodeID) {
      this.showLinkToHubReportCheckboxes = true;
      this.linkToUboTrees = this.uboTreeService.linkToHubReportNodesDistinctByHubReport.value;
    }
  }

  protected onSendEmailToggled() {
    if (this.sendEmail) {
      this.prefillAuthenticatedUserEmail();
    } else {
      this.emailsControl.setErrors(null);
    }
  }

  protected onEmailTokenEnd(event: MatChipInputEvent) {
    this.addEmails(event.value);
  }

  // Allows pasting a list of emails, e.g. "a@b.com, c@d.com"
  protected onEmailPaste(event: ClipboardEvent) {
    const pasted = event.clipboardData?.getData('text');

    if (!pasted || !ConfirmSubmitComponent.emailSeparators.test(pasted)) {
      // Single email - let the browser paste it into the input as usual
      return;
    }

    event.preventDefault();
    this.addEmails(pasted);
  }

  protected removeEmail(email: string) {
    this.emails = this.emails.filter(existing => existing !== email);
    this.validate();
  }

  protected onSubmit() {
    if (this.sendEmail) {
      // Whatever is still sitting in the input counts as an email the user meant to add
      this.addEmails(this.pendingEmail.value ?? '');

      if (this.emailsControl.invalid) {
        return;
      }
    }

    this.dialogRef.close({
      submit: true,
      shouldSendEmail: this.sendEmail,
      targetEmails: this.sendEmail ? this.emails : [],
      shouldReturnToBusinessSearchResults: this.shouldReturnToBusinessSearchResults,
      uboTreesToLinkTo: this.uboTreeNodesToLinkTo
    });
  }

  protected onHubReportCheckboxClick(node: UboTreeNode) {
    if (this.uboTreeToLinkTo === node) {
      this.uboTreeToLinkTo = undefined;
      this.uboTreeNodesToLinkTo = undefined;
    } else {
      this.uboTreeToLinkTo = node;
      this.uboTreeNodesToLinkTo = this.uboTreeService.linkToHubReportNodes.value.filter(n => n.hubReportID === node.hubReportID);
    }
  }

  protected getDestination() {
    return this.shouldReturnToBusinessSearchResults ? Constants.returnToOriginatingOrder : Constants.stayOnNewOrder;
  }


  // Turns every valid email in the value into a chip. Anything invalid is left in the
  // input so it can be corrected and flags the error that blocks submission.
  private addEmails(value: string) {
    const entries = value
      .split(ConfirmSubmitComponent.emailSeparators)
      .map(entry => entry.trim())
      .filter(entry => entry.length > 0);

    const invalid: string[] = [];

    for (const email of entries) {
      if (!this.isValidEmail(email)) {
        invalid.push(email);
      } else if (!this.emails.some(existing => existing.toLowerCase() === email.toLowerCase())) {
        this.emails = [...this.emails, email];
      }
    }

    this.pendingEmail.setValue(invalid.join(', '), { emitEvent: false });
    this.validate();
  }

  private isValidEmail(email: string): boolean {
    return Validators.email({ value: email } as AbstractControl) === null;
  }

  private prefillAuthenticatedUserEmail() {
    if (this.emails.length === 0 && this.authenticatedUserEmail) {
      this.addEmails(this.authenticatedUserEmail);
    } else {
      this.validate();
    }
  }

  private validate() {
    const pending = (this.pendingEmail.value ?? '').trim();
    const errors: { [key: string]: boolean } = {};

    if (pending && !this.isValidEmail(pending)) {
      errors['invalidEmail'] = true;
    }

    if (this.emails.length === 0 && !pending) {
      errors['required'] = true;
    }

    const hasErrors = Object.keys(errors).length > 0;
    this.emailsControl.setErrors(hasErrors ? errors : null);

    if (hasErrors) {
      // mat-form-field only shows errors for a touched control
      this.emailsControl.markAsTouched();
    }
  }

  private clearInvalidEmailError() {
    if (!this.emailsControl.hasError('invalidEmail')) {
      return;
    }

    const { invalidEmail, ...remaining } = this.emailsControl.errors ?? {};
    this.emailsControl.setErrors(Object.keys(remaining).length > 0 ? remaining : null);
  }
}
